import { APP_VERSION } from '@/version';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Monitor, ImageIcon, Upload, LayoutGrid, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

function PublicHeader() {
  return (
    <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center space-x-4">
            <img
              src="/curling-stadium-logo.png"
              alt="Curling Stadium Manager"
              className="h-8 w-8 object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <span className="font-bold text-xl">Curling Stadium Manager</span>
            <Badge variant="secondary">v{APP_VERSION}</Badge>
          </div>
          <nav className="flex items-center space-x-4">
            <Button variant="ghost" asChild>
              <Link to="/">Home</Link>
            </Button>
            <Button asChild>
              <Link to="/login">Sign In</Link>
            </Button>
          </nav>
        </div>
      </div>
    </header>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-muted-foreground">
          <p>&copy; 2026 Curling Stadium Manager. Built for the curling broadcast community.</p>
        </div>
      </div>
    </footer>
  );
}

export default function AboutOverlaysPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
      <PublicHeader />

      {/* Hero */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary mb-6">
            <Monitor className="h-4 w-4" />
            Overlays &amp; Slideshows
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
            Your sponsors on screen.<br />
            <span className="text-primary">No broadcast crew required.</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            CSM lets your club upload sponsor logos, build rotating slideshows, and display professional
            broadcast graphics — all without touching OBS settings or hiring a production team.
          </p>
        </div>
      </section>

      {/* Slideshows */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-2">Sponsor Slideshows</h2>
          <p className="text-muted-foreground text-lg mb-10">
            A slideshow is a rotating loop of sponsor banners that plays inside your broadcast overlay
            during games. Each sponsor gets their logo on screen for a set number of seconds before the
            next one appears.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <Card>
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-blue-500/10 flex items-center justify-center mb-2">
                  <Upload className="h-6 w-6 text-blue-500" />
                </div>
                <CardTitle>1. Upload Sponsor Logos</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground">
                Your club logs into CSM and uploads sponsor banner images — just PNG files sized to fit
                the overlay. No design software needed; we provide a blank template to start from.
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-green-500/10 flex items-center justify-center mb-2">
                  <LayoutGrid className="h-6 w-6 text-green-500" />
                </div>
                <CardTitle>2. Build Your Slideshow</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground">
                Arrange the logos in any order and set how long each one displays. Mark one slideshow
                as "active" and it will automatically play during every broadcast.
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="h-12 w-12 rounded-lg bg-purple-500/10 flex items-center justify-center mb-2">
                  <Monitor className="h-6 w-6 text-purple-500" />
                </div>
                <CardTitle>3. It Appears on Screen</CardTitle>
              </CardHeader>
              <CardContent className="text-muted-foreground">
                The slideshow shows up automatically inside your broadcast overlay. No further action
                needed — update your sponsors in CSM and the change goes live immediately.
              </CardContent>
            </Card>
          </div>

          <Card className="bg-muted/40 border-muted">
            <CardContent className="p-6">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Self-service.</strong> Facility staff can log in, upload new sponsors, reorder the slideshow, and set durations
                entirely on their own — no administrator involvement required. Changes take effect the next
                time OBS refreshes the overlay, usually within seconds.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Overlays */}
      <section className="bg-muted/30 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-2">Broadcast Overlays</h2>
            <p className="text-muted-foreground text-lg mb-10">
              An overlay is the layer of graphics that sits on top of your camera feed in OBS —
              the scoreboard, the sponsor banner area, the event ticker. CSM lets you build and manage
              those graphics without any coding.
            </p>

            <div className="mb-10">
              <h3 className="text-xl font-semibold mb-4">What's in an overlay?</h3>
              <ul className="space-y-3 max-w-lg">
                {[
                  { label: 'Slideshow widget', desc: 'The rotating sponsor banner area' },
                  { label: 'Scoreboard', desc: 'Team names, ends, and scores' },
                  { label: 'Clock / timer', desc: 'Game or shot clock' },
                  { label: 'Event ticker', desc: 'Scrolling text for event info' },
                  { label: 'Logo / watermark', desc: 'Club or broadcast branding' },
                ].map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-foreground">{item.label}</strong>
                      <span className="text-muted-foreground"> — {item.desc}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <Card className="bg-muted/40 border-muted">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <ImageIcon className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    <strong className="text-foreground">One overlay, many uses.</strong> The same overlay URL works in OBS, vMix, Wirecast,
                    or any browser. You can even open it in a regular browser window to preview exactly what
                    your viewers will see before going live.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Who is this for */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center">Who manages what?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-blue-200 dark:border-blue-800">
              <CardHeader>
                <CardTitle className="text-blue-600 dark:text-blue-400">Your Club Staff</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-muted-foreground">
                <p>✓ Upload and replace sponsor logos</p>
                <p>✓ Set how long each sponsor appears</p>
                <p>✓ Switch between seasonal slideshows</p>
                <p>✓ Preview changes before they go live</p>
              </CardContent>
            </Card>
            <Card className="border-purple-200 dark:border-purple-800">
              <CardHeader>
                <CardTitle className="text-purple-600 dark:text-purple-400">CSM Administrator</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-muted-foreground">
                <p>✓ Build and design overlay layouts</p>
                <p>✓ Set up new facilities and sheets</p>
                <p>✓ Configure OBS browser source URLs</p>
                <p>✓ Manage access for facility accounts</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="max-w-2xl mx-auto text-center space-y-4">
          <Button variant="outline" asChild>
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
          <span className="mx-4 text-muted-foreground">or</span>
          <Button asChild>
            <Link to="/about/streaming">
              Learn about Automated Streaming
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
